import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/Ledger.css';

const Ledger = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.selectedUser) {
      const u = location.state.selectedUser;
      const formatted = {
        id: u.id,
        name: u.firstName ? `${u.firstName} ${u.lastName}` : (u.name || ''),
        email: u.email,
        createdAt: u.createdAt,
        totalRoundupAmount: parseFloat(u.chipAmount || 0) 
      };
      setSelectedUser(formatted);
    }
  }, [location.state]);

  useEffect(() => {
    fetchUsersWithRoundups();
  }, []);

  // Sync selectedUser with updated users list (to get transactions if navigating from other pages)
  useEffect(() => {
    if (users.length > 0 && selectedUser && !selectedUser.transactions) {
      const fullUser = users.find(u => u.id === selectedUser.id);
      if (fullUser && fullUser.transactions) {
        setSelectedUser(fullUser);
      }
    }
  }, [users, selectedUser]);

  const fetchUsersWithRoundups = async () => {
    try {
      const res = await api.get('/admin-management/users-with-roundups');

      const apiUsers = res.data?.users || [];

      const formatted = apiUsers.map(u => ({
        id: u.id,
        name: `${u.firstName} ${u.lastName}`,
        email: u.email,
        role: u.role,
        createdAt: u.createdAt,
        totalRoundupAmount: parseFloat(u.totalRoundupAmount || 0),
        transactions: (u.transactions || []).map(t => {
          const absAmount = Math.abs(t.amount);
          const roundup = Math.ceil(absAmount) - absAmount;
          return {
            id: t.id,
            date: new Date(t.transactionDate).toLocaleDateString('en-US'),
            rawDate: new Date(t.transactionDate),
            description: t.merchantName,
            amount: roundup,
            originalAmount: t.amount
          };
        })
      }));

      setUsers(formatted);

    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.clear();
        window.location.href = '/login';
        return;
      }
      console.error('Ledger API error:', err?.response?.data || err.message);
    }
  };


  const UserSummary = () => (
    <div className="ledger-summary-view">
      <div className="ledger-table-wrapper">

        <table className="ledger-table">
          <thead>
            <tr>
              <th>User</th>
              <th className="hide-mobile">Email</th>
              <th className="hide-mobile">Join Date</th>
              <th>Total</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {(() => {
              const filteredUsers = users.filter(u =>
                u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                u.email.toLowerCase().includes(searchTerm.toLowerCase())
              );

              if (filteredUsers.length === 0) {
                return (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                      No round-ups found.
                    </td>
                  </tr>
                );
              }

              return filteredUsers.map(user => (
                <tr
                  key={user.id}
                  className="clickable-row"
                  onClick={() => setSelectedUser(user)}
                >
                  <td className="user-cell">
                    <div className="user-avatar-mini">
                      {user.name?.charAt(0)}
                    </div>
                    <span>{user.name}</span>
                  </td>

                  <td className="hide-mobile">{user.email}</td>

                  <td className="hide-mobile">
                    {new Date(user.createdAt).toLocaleDateString('en-US')}
                  </td>

                  <td className="amount-positive">
                    ${user.totalRoundupAmount.toFixed(2)}
                  </td>

                  <td>
                    <button className="view-btn">View</button>
                  </td>
                </tr>
              ));
            })()}
          </tbody>

        </table>
      </div>
    </div>
  );

  const UserDetail = ({ user }) => {
    // Sort transactions by date (ascending) to calculate running balance
    const sortedTxns = [...(user.transactions || [])].sort((a, b) => a.rawDate - b.rawDate);

    let runningBalance = 0;
    const transactionsWithBalance = sortedTxns.map(t => {
      runningBalance += t.amount;
      return { ...t, balance: runningBalance };
    });

    // Reverse for display (newest first)
    const displayTransactions = [...transactionsWithBalance].reverse();

    return (
      <div className="ledger-detail-view">
        <div className="ledger-table-wrapper">

          <table className="ledger-table">
            <thead>
              <tr>
                <th>Date</th>
                <th className="hide-mobile">Description</th>
                <th>Amount</th>
                <th>Round-Up</th>
                <th>Total</th>
              </tr>
            </thead>

            <tbody>
              {displayTransactions.length > 0 ? (
                displayTransactions.map(entry => (
                  <tr key={entry.id}>
                    <td className="date-cell">{entry.date}</td>
                    <td className="hide-mobile">{entry.description}</td>
                    <td className="hide-mobile">${entry.originalAmount}</td>
                    <td className="amount-cell positive">
                      +${entry.amount.toFixed(2)}
                    </td>
                    <td className="balance-cell">
                      ${entry.balance.toFixed(2)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                    No round-up transactions found for this user.
                  </td>
                </tr>
              )}
            </tbody>

          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="ledger-container">

      <div className="finance-header-section ledger-header">
        {selectedUser ? (
          <>
            <button
              className="back-pill"
              onClick={() => {
                if (location.state?.from) {
                  navigate(location.state.from);
                } else {
                  setSelectedUser(null);
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              <span>Back</span>
            </button>

            <div className="header-user-info">
              <div className="user-avatar-mini large">
                {selectedUser.name?.charAt(0)}
              </div>
              <div>
                <h1>{selectedUser.name}</h1>
                <p>{selectedUser.email}</p>
              </div>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
            <div className="overview-section">
              <div className="overview-header">
                <p style={{ color: 'var(--text-muted)', fontSize: '14px', fontWeight: '500', margin: 0 }}>Accumulated additions across all users</p>
              </div>
            </div>

            <div className="search-container visible-search">
              <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search by name or email..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {!selectedUser ? <UserSummary /> : <UserDetail user={selectedUser} />}

    </div>
  );
};

export default Ledger;